import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { DialogProps } from "@mui/material/Dialog/Dialog";
import { CloseDialogType } from "organisms/ButtonOrMenuItemWithDialog";
import { createContext, ReactNode, useState } from "react";
import { Confirm } from "react-admin";

import ClosableDialogTitle from "./ClosableDialogTitle";

type DialogLeaveWarningContextType = {
  formIsDirty: boolean;
  setFormIsDirty: (formIsDirty: boolean) => void;
  isInsideDialog: boolean;
};

export const DialogLeaveWarningContext = createContext<DialogLeaveWarningContextType>({
  formIsDirty: false,
  setFormIsDirty: () => {
    return;
  },
  isInsideDialog: false,
});

export type EdmDialogPropsType = Omit<DialogProps, "children" | "title"> & {
  children: (closeDialog: CloseDialogType) => ReactNode;
  actions?: (closeDialog: CloseDialogType) => ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode | string;
  titleActions?: ReactNode;
};

export default function EdmDialog(props: EdmDialogPropsType) {
  const { actions, children, onClose, title = "", subtitle, titleActions, open = false, ...dialogProps } = props;
  const [formIsDirty, setFormIsDirty] = useState<boolean>(false);
  const [confirmIsOpen, setConfirmIsOpen] = useState<boolean>(false);

  const closeDialog: CloseDialogType = (e, reason, force = false) => {
    if (formIsDirty && !force) {
      setConfirmIsOpen(true);
    } else {
      handleClose(e, reason);
    }
  };

  const onConfirm = () => {
    handleClose();
    setConfirmIsOpen(false);
  };

  const handleClose = (e?: object, reason?: "backdropClick" | "escapeKeyDown") => {
    onClose?.(e || "", reason || "backdropClick");
  };

  return (
    <>
      <DialogLeaveWarningContext.Provider value={{ formIsDirty, setFormIsDirty, isInsideDialog: true }}>
        <Dialog
          onClose={closeDialog}
          open={open}
          {...dialogProps}
          onKeyDown={(event) => {
            if (event.key === "Tab" || event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.stopPropagation();
            }
          }}
        >
          <ClosableDialogTitle closeDialog={closeDialog} subtitle={subtitle} actions={titleActions}>
            {title}
          </ClosableDialogTitle>
          <DialogContent>{open && children(closeDialog)}</DialogContent>
          {actions && <DialogActions>{actions(closeDialog)}</DialogActions>}
        </Dialog>
      </DialogLeaveWarningContext.Provider>
      <Confirm
        isOpen={confirmIsOpen}
        title="ra.action.confirm"
        content="confirmations.unsaved_changes_dialog"
        onConfirm={onConfirm}
        onClose={() => setConfirmIsOpen(false)}
        maxWidth="md"
      />
    </>
  );
}
